<template>
    <validation-observer ref="observer" v-slot="{ handleSubmit }">
        <b-form id="company-form"
                autocomplete="new-password"
                @submit.stop.prevent="handleSubmit(saveEditData)"
                @reset="resetForm"
        >
            <dl class="description-list-wrapper">
                <DescriptionItem :label="$t('기업명')" mode="form" labelFor="edit-form-company-name"
                                 required
                >
                    <div class="w-100">
                        <validation-provider
                            v-slot="validationContext"
                            name="edit-form-company-name"
                            :rules="{ required: true, min: 4 }"
                        >
                            <b-form-input id="edit-form-company-name"
                                          v-model="form.company_name"
                                          name="edit-form-company-name"
                                          :disabled="editLoading"
                                          :placeholder="$t('기업명')"
                                          :state="getValidationState(validationContext)"
                                          autocomplete="new-password"
                                          aria-describedby="edit-form-company-name-feedback"
                            />
                            <b-form-invalid-feedback id="edit-form-company-name-feedback">
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </validation-provider>
                    </div>
                </DescriptionItem>
                <DescriptionItem :label="$t('사업자등록번호')" mode="form" labelFor="edit-form-company-code">
                    <b-form-input id="edit-form-company-code"
                                  v-model="form.company_code"
                                  :disabled="editLoading"
                                  :placeholder="$t('사업자등록번호')"
                                  autocomplete="new-password"
                    />
                </DescriptionItem>

                <DescriptionItem :label="$t('주소')" mode="form" labelFor="edit-form-company-address">
                    <b-form-input id="edit-form-company-address"
                                  v-model="form.company_address"
                                  :disabled="editLoading"
                                  :placeholder="$t('주소')"
                                  autocomplete="new-password"
                    />
                </DescriptionItem>

                <DescriptionItem :label="$t('전화번호')" mode="form" labelFor="edit-form-company-tel">
                    <b-form-input id="edit-form-company-tel"
                                  v-model="form.company_tel"
                                  :disabled="editLoading"
                                  :placeholder="$t('전화번호')"
                                  autocomplete="new-password"
                    />
                </DescriptionItem>


                <DescriptionItem :label="$t('원청사 유무')" mode="form" labelFor="edit-form-client" required> 
                    <validation-provider
                        v-slot="validationContext"
                        name="edit-form-client"
                        :rules="{ required: true }"
                    >
                        <b-form-radio-group
                            id="edit-form-client"
                            v-model="form.client"
                            :options="client_options"
                            button-variant="outline-success"
                            :state="getValidationState(validationContext)"
                            size="sm"
                            name="edit-form-client"
                            buttons
                            aria-describedby="edit-form-client-feedback"
                        />
                        <b-form-invalid-feedback id="edit-form-client-feedback"
                                                 class="d-block"
                                                 :class="{'mt-0': getValidationState(validationContext) !== false }"
                        >
                            {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                    </validation-provider>
                </DescriptionItem>


            </dl>
        </b-form>
    </validation-observer>
</template>

<script>
import DescriptionItem from '@shared/components/common/DescriptionItem.vue';
import {getErrorMessage} from '@core/utils/api/errorMessage';

// 미저장 판정 대상 (저장 시 보내는 입력 칸)
const EDITABLE_KEYS = ['company_name', 'company_code', 'company_address', 'company_tel', 'client'];
const pickEditable = (form) => EDITABLE_KEYS.reduce((acc, key) => ({...acc, [key]: form[key] == null ? '' : String(form[key])}), {});

export default {
    name: 'CompanyForm',
    components: {
        DescriptionItem,
    },
    props: {
        detailData: {
            type: [Object, null],
            default: null,
        },
        mode: {
            type: String,
            required: true,
            default: 'add', // add | edit
        }
    },

    data() {
        return {
            editLoading: false,
            form: {
                id: null,
                index: null,
                company_name: null,
                company_code: null,
                company_address: null,
                company_tel: null,
                client: 0,
            },
            client_options: [
                {value: 1, text: this.$t('원청사')},
                {value: 0, text: this.$t('평가대상 기업')},
            ],
            savedForm: {},
        };
    },
    computed: {
        // 부모가 저장 버튼 상태·이탈 확인에 쓴다 (@saving, @dirty)
        dirty() {
            const current = pickEditable(this.form);
            return EDITABLE_KEYS.some(key => current[key] !== this.savedForm[key]);
        },
    },
    watch: {
        detailData: {
            immediate: true,
            handler() {
                this.form = {...this.form, ...this.detailData};
                this.savedForm = pickEditable(this.form);
            }
        },
        dirty(value) {
            this.$emit('dirty', value);
        },
        editLoading(value) {
            this.$emit('saving', value);
        },
    },
    beforeDestroy() {
        this.$emit('dirty', false);
        this.$emit('saving', false);
    },
    methods: {
        getValidationState({dirty, validated, valid = null}) {
            return dirty || validated ? valid : null;
        },
        async saveEditData() {
            // Enter 제출 연타 방지
            if (this.editLoading) return;
            try {
                this.editLoading = true;

                // null/undefined 를 append 하면 'null'/'undefined' 문자열로 저장되므로 값이 있는 키만 보낸다
                const formData = new FormData();
                ['company_id', 'company_name', 'company_code', 'company_address', 'company_tel', 'client'].forEach(key => {
                    // 등록 모드에서 늦게 도착한 직전 기업 정보의 id 가 섞이지 않게 한다
                    if (key === 'company_id' && this.mode !== 'edit') return;
                    if (this.form[key] !== null && this.form[key] !== undefined) {
                        formData.append(key, this.form[key]);
                    }
                });

                const response = await this.$axios.post('/company/', formData);

                this.$EventBus.$emit('toast-success', this.$t('기업 추가 및 수정'), this.$t('기업 정보가 성공적으로 저장되었습니다.'));
                // 저장한 값이 새 기준 — 이후 편집만 미저장으로 본다
                this.savedForm = pickEditable(this.form);
                this.$emit('updateDetailData', {...this.detailData, ...this.form, ...response.data});
            } catch (error) {
                console.error(error);
                console.dir(error);
                this.$EventBus.$emit('toast-failure', this.$t('기업 추가 및 수정'), getErrorMessage(error, '기업 데이터 저장 도중 오류가 발생했습니다. 관련 기업 검색을 재검색해 편집 및 저장해주세요.'));
            } finally {
                this.editLoading = false;
            }
        },
        resetForm() {
            this.form = {
                id: null,
                index: null,
                company_name: null,
                company_code: null,
                company_address: null,
                company_tel: null,
                client: null,
            };
        },
    },
};
</script>

<style scoped lang="scss">

</style>
